import { Badge } from "@/components/ui/badge";
import { formatDuration } from "@/lib/formatters";
import { formatPappersNumber, type PappersStatsView } from "@/lib/pappersStats";

interface PappersRunStatsProps {
  stats: PappersStatsView;
  compact?: boolean;
  durationSeconds?: number | null;
}

export function PappersRunStats({ stats, compact = false, durationSeconds }: PappersRunStatsProps) {
  if (compact) return (
    <div className="flex flex-wrap gap-1.5">
      {stats.isMock && <Badge variant="secondary">Mock data</Badge>}
      {stats.summary.map(metric => (
        <span key={metric.key} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
          <span>{metric.label}:</span>
          <span className="font-medium text-slate-700">{formatPappersNumber(metric.value)}</span>
        </span>
      ))}
      {stats.outcomeNotes.length > 0 && (
        <span className="basis-full break-words text-[10px] text-slate-500">{stats.outcomeNotes.join(" · ")}</span>
      )}
    </div>
  );

  return (
    <div className="space-y-4 rounded-xl bg-white p-4 shadow-card-rest">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-slate-900">Pappers run statistics</h3>
        {stats.isMock && <Badge variant="secondary">Mock data</Badge>}
      </div>
      {stats.isMock && <p className="text-xs text-slate-500">Simulated values; no real provider debit is inferred.</p>}
      {[{ name: "Run summary", metrics: stats.summary, basis: undefined }, ...stats.endpoints].map(section => (
        <section key={section.name} className="space-y-2">
          <h4 className="text-xs font-medium text-slate-600">{section.name}</h4>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
            {section.metrics.map(metric => (
              <div key={metric.key} className="min-w-0 space-y-0.5">
                <dt className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{metric.label}</dt>
                <dd className="break-words text-lg font-semibold tabular-nums text-slate-900">
                  {formatPappersNumber(metric.value)}
                  {metric.note && <span className="block text-xs font-normal text-slate-500">{metric.note}</span>}
                </dd>
              </div>
            ))}
          </dl>
          {section.basis && <p className="break-words text-xs text-slate-500">Estimate basis: {section.basis}</p>}
        </section>
      ))}
      {durationSeconds != null && <p className="text-xs text-slate-500">Duration: {formatDuration(durationSeconds)}</p>}
      <div className="space-y-1 border-t border-slate-900/[0.06] pt-3">
        {[...stats.outcomeNotes, ...stats.notes].map(note => <p key={note} className="break-words text-xs text-slate-500">{note}</p>)}
      </div>
      {Object.keys(stats.otherStats).length > 0 && (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer">Other reported statistics</summary>
          <pre className="mt-2 whitespace-pre-wrap break-words">{JSON.stringify(stats.otherStats, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}
